import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import WithProcessError from 'hocs/WithProcessError';
import PropTypes from 'prop-types';

import Button from 'components/Button/Button';
import ContentFooter from 'components/ContentFooter/ContentFooter';
import FormGroup from 'components/FormGroup/FormGroup';
import FormItem from 'components/FormItem/FormItem';
import Input from 'components/Input/Input';
import Panel from 'components/Panel/Panel';
import PanelHeader from 'components/PanelHeader/PanelHeader';
import Popup from 'components/Popup/Popup';
import RichCard from 'components/RichCard/RichCard';

import AppCreateTelegram from './AppCreate/AppCreateTelegram';
import AppCreateWebhook from './AppCreate/AppCreateWebhook';
import { AppInstall } from './AppInstall';

import Add24 from 'icons/Add24';
import Close24 from 'icons/Close24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import declOfNum from 'utils/declOfNum';
import { actions } from 'utils/permissions';

import styles from './apps.scss';

const SIDE_PANELS_WIDTH = 340;

const APP_NAME = {
    Telegram: 'telegram',
    Webhook: 'webhook',
};

const APP_DESCRITION = {
    [APP_NAME.Telegram]: 'Оповещения через Telegram',
    [APP_NAME.Webhook]: 'Вызов Webhook',
};

function Apps(props) {
    const auth = useAuth();
    const api = useApi();
    const queryClient = useQueryClient();
    const companyId = auth?.company.id;

    const { processError } = props;

    const [popup, setPopup] = React.useState(null);

    const [create, setCreate] = React.useState(false);
    const [setup, setSetup] = React.useState(false);

    const { data } = useQuery({
        queryKey: ['applications', 'company-' + companyId],
        queryFn: getApplications,
        staleTime: 5 * 1000,
        retry: 0,
    });

    const deleteAppMutation = useMutation({
        mutationFn: (appId) => {
            return api.deleteApplication(companyId, appId);
        },
        onSuccess: (data) => {
            const { id } = data || {};

            if (setup.id === id) {
                setSetup(false);
            }

            queryClient.invalidateQueries({ queryKey: ['applications', 'company-' + companyId] });
        },
        onError: (error) => {
            processError('Приложение не удалено', error);
        },
    });

    async function getApplications() {
        try {
            const result = await api.getApplications(companyId);

            return result?.applications || [];
        } catch (e) {
            processError('Не удалось загрузить приложения', e);
        }
    }

    function handleSetup(app) {
        setSetup(app);
        setCreate(false);
    }

    function handleDelete(id) {
        setPopup(
            <Popup
                onClosed={onPopupClosed}
                title={'Удалить приложение'}
                canCloseOutside={true}
                confirmDanger={true}
                onConfirm={() => handleDeleteConfirm(id)}
                confirmText={'Удалить'}
                autoclose={true}
                description={'Вы уверены, что хотите удалить это приложение?'}
            />
        );
    }

    function handleDeleteConfirm(appId) {
        deleteAppMutation.mutate(appId);
    }

    function handleCloseSetup() {
        setSetup(false);
        setCreate(false);
    }

    /**
     * Triggers when user clicks "Add" button
     * on application item.
     */
    function onAddApplication(appName) {
        setSetup(false);
        setCreate(appName);
        setPopup(() => prepareAppAddPopup(true));
    }

    function onPopupClosed() {
        setPopup(null);
    }

    function onAppCreated() {
        setCreate(false);
    }

    function prepareAppAddPopup(closePopup) {
        return (
            <Popup
                shown={true}
                maxWidth={480}
                close={closePopup}
                noConfirm={true}
                canCloseOutside={true}
                title="Добавить приложение"
                description={'Выберите приложение из списка ниже.'}
                onClosed={onPopupClosed}
            >
                <div className={styles.appsCatalogue}>
                    <AppInstall
                        name="Оповещения через Telegram"
                        description="Получайте уведомления об оплатах."
                        iconPath="url(/assets/telegram.png)"
                        onAddClick={() => onAddApplication(APP_NAME.Telegram)}
                    />

                    <AppInstall
                        name="Оповещения через Webhook"
                        description="Получайте уведомления об оплатах и подписках по webhook."
                        iconPath="url(/assets/webhook.png)"
                        onAddClick={() => onAddApplication(APP_NAME.Webhook)}
                    />
                </div>
            </Popup>
        );
    }

    function handleApplicationAddClick() {
        setPopup(() => prepareAppAddPopup(false));
    }

    function getAppsActions() {
        if (auth.hasPermission(actions.CREATE_APPS)) {
            return (
                <Button
                    text="Добавить приложение"
                    icon={<Add24 />}
                    onClick={handleApplicationAddClick}
                    level={'secondary'}
                />
            );
        }

        return null;
    }

    function prepareAppsToRender() {
        /**
         * @typedef Application
         * @type {object}
         * @property {number} id Unique id of app.
         * @property {string} type Type of application (Telegram for now).
         * @property {number} external_id ID of telegram chat.
         * @property {string} name Name of application, specified by user.
         * @property {string} token Application token.
         * @property {string} created_at Creation date.
         * @property {string} updated_by Updated by user.
         * @property {string} deactivated_at Deactivated when.
         * @property {boolean} active Is application active.
         */

        /** @type {Application[]} */
        const apps = data || [];

        return apps.map((app) => {
            return (
                <RichCard
                    key={app.id}
                    style={{ padding: '1rem 0' }}
                    title={app.name}
                    description={APP_DESCRITION[app.type]}
                    bottom={
                        <div className={styles.appActions}>
                            {app.type === APP_NAME.Telegram && (
                                <Button
                                    text="Добавить в чат"
                                    onClick={() => handleSetup(app)}
                                    level={'tertiary'}
                                    size={'s'}
                                />
                            )}
                            <Button text="Удалить" level={'tertiary'} onClick={() => handleDelete(app.id)} size={'s'} />
                        </div>
                    }
                />
            );
        });
    }

    const applications = prepareAppsToRender();

    return (
        <>
            {popup}
            <Panel fill={true} header={<PanelHeader title="Приложения" after={getAppsActions()} />}>
                <div className={styles.appsList}>{applications}</div>
                <ContentFooter>{`${applications.length > 0 ? applications.length : 'Нет'} ${declOfNum(
                    applications.length,
                    ['приложение', 'приложения', 'приложений']
                )}`}</ContentFooter>
            </Panel>
            {create === APP_NAME.Telegram && (
                <AppCreateTelegram
                    panelWidth={SIDE_PANELS_WIDTH}
                    onCreated={onAppCreated}
                    onBackClick={handleCloseSetup}
                />
            )}
            {create === APP_NAME.Webhook && (
                <AppCreateWebhook
                    panelWidth={SIDE_PANELS_WIDTH}
                    onCreated={onAppCreated}
                    onBackClick={handleCloseSetup}
                />
            )}
            {setup && (
                <Panel
                    separated={true}
                    header={
                        <PanelHeader
                            title={'Оповещения Telegram'}
                            back={true}
                            onBackClick={handleCloseSetup}
                            backIcon={<Close24 />}
                        />
                    }
                    contentStyles={{ maxWidth: SIDE_PANELS_WIDTH, minWidth: SIDE_PANELS_WIDTH, padding: '0 0.5rem' }}
                >
                    <FormGroup>
                        <FormItem>
                            <p className={styles.appDescription}>
                                Добавьте бота{' '}
                                <a href="https://t.me/DispurseBot" target="_blank" rel="noreferrer">
                                    @DispurseBot
                                </a>{' '}
                                в&nbsp;личный или групповой чат и напишите:
                            </p>
                        </FormItem>
                        <FormItem>
                            <pre className={styles.botCommand}>/register СЕКРЕТНЫЙ_КЛЮЧ_БОТА</pre>
                        </FormItem>
                    </FormGroup>
                    <FormGroup>
                        <FormItem label={'Название'}>
                            <Input name="app-name" disabled={true} value={setup.name} />
                        </FormItem>
                        <FormItem
                            label={'Секретный ключ бота'}
                            required={true}
                            caption={'Двойной клик, чтобы выделить весь ключ.'}
                        >
                            <Input name="telegram-key" disabled={true} value={setup.token} />
                        </FormItem>
                    </FormGroup>
                </Panel>
            )}
        </>
    );
}

Apps.propTypes = {
    processError: PropTypes.func,
};

export default WithProcessError(Apps);
